/**
 * components/accounting/TransactionTable.tsx
 * Transactions table with inline edit and delete actions.
 * - Filters by gateway and shows filtered totals.
 * - Locked when selected month is closed.
 * - Entry users: no edit/delete and no totals bar.
 * - Viewer: sees totals bar (full details) but cannot edit/delete.
 */

import { use, useMemo, useState } from 'react'
import { formatCurrency, formatDate } from '../../lib/format'
import { CheckCircle2, Edit3, Trash2, CircleDot } from 'lucide-react'
import { isAfter, startOfDay } from 'date-fns'
import type { Transaction } from '../../types/entites'
import { useSelectedMonth } from '../../hooks/useMonth'
import { useAccountingStore as useAcc } from '../../store/accounting'
import { useUserStore } from '@/store/user'

interface RowEdit {
  id: string
  description: string
  gateway: string
  amountGross: string
  depositDate: string
}

interface TransactionTableProps {
  monthRange?: { start: Date; end: Date } | null
}

function withinMonth(t: Transaction, range?: { start: Date; end: Date } | null) {
  if (!range) return true
  if (t.deposited && t.deposited_at) {
    const d = new Date(t.deposited_at)
    return d >= range.start && d <= range.end
  }
  const d = new Date(t.deposit_date)
  return d >= range.start && d <= range.end
}

export function TransactionTable({ monthRange = null }: TransactionTableProps) {

  const transactions = useUserStore((s) => s.transactions);
  const updateTransaction = useUserStore((s) => s.updateTransaction)
  const removeTransaction = useUserStore((s) => s.removeTransaction)
  const gateways = useUserStore((s) => s.gateways)

  const [editing, setEditing] = useState<RowEdit | null>(null)

  const [gatewayFilter, setGatewayFilter] = useState<string>('الكل')

  // Lock & role
  const { month } = useSelectedMonth()
  const getClosure = useAcc((s) => s.getClosure)
  const isClosed = Boolean(getClosure(month))

  const role = useUserStore((s) => s.role)
  const isAdmin = role === 'admin'
  const isViewer = role === 'viewer'
  const noEditMode = isClosed || !isAdmin

  const filtered: Transaction[] = useMemo(() => {
    console.log(monthRange);
    return transactions.filter((t) => {
      const passGateway = gatewayFilter === 'الكل' || t.gateway === gatewayFilter
      const passMonth = withinMonth(t, monthRange || undefined)
      return passGateway && passMonth
    })
  }, [transactions, gatewayFilter, monthRange])

  const filteredTotals = useMemo(() => {
    const totalNet = filtered.reduce((s, t) => s + t.net_amount, 0)
    return { count: filtered.length, totalNet }
  }, [filtered])

  const ordered: Transaction[] = useMemo(() => {
    return [...filtered].sort((a, b) => (a.created_at < b.created_at ? 1 : -1))
  }, [filtered])

  const onEdit = (id: string) => {
    if (noEditMode) return
    const t = transactions.find((x) => x.id === id)
    if (!t) return
    setEditing({
      id,
      description: t.description,
      gateway: t.gateway,
      amountGross: String(t.amount_gross),
      depositDate: t.deposit_date.slice(0, 10),
    })
  }

  const onSave = () => {
    if (!editing || noEditMode) return
    const gross = Number(editing.amountGross)
    if (isNaN(gross) || gross <= 0) return
    updateTransaction(editing.id, {
      description: editing.description,
      gateway: editing.gateway as any,
      amount_gross: gross,
      deposit_date: editing.depositDate,
    })
    setEditing(null)
  }

  const onCancel = () => setEditing(null)

  const onDelete = (id: string) => {
    if (noEditMode) return
    const ok = confirm('تأكيد حذف العملية؟ لا يمكن التراجع.')
    if (ok) removeTransaction(id)
  }

  const gatewayNames = useMemo(() => gateways.map((g) => g.name), [gateways])
  const gatewayOptions = useMemo(() => ['الكل', ...gatewayNames], [gatewayNames])

  return (
    <div className="space-y-3">
      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="mb-1 block text-xs text-slate-600">بوابة الدفع</label>
          <select
            value={gatewayFilter}
            onChange={(e) => setGatewayFilter(e.target.value)}
            className="rounded border border-slate-300 bg-white px-2 py-1 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
          >
            {gatewayOptions.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Filtered totals: show for admin and viewer */}
      {(isAdmin || isViewer) && (
        <div
          className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
          title="إجمالي نتائج الفلترة الحالية"
        >
          <span className="text-slate-700">إجمالي المبالغ لنتيجة الفلترة</span>
          <span className="font-semibold text-slate-900">
            {formatCurrency(filteredTotals.totalNet)} • عدد: {filteredTotals.count}
          </span>
        </div>
      )}

      {isClosed && (
        <div className="rounded-lg bg-amber-50 p-2 text-sm text-amber-800">الشهر مُقفَل. التعديل/الحذف معطّل.</div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-3 py-2 text-right font-medium text-gray-700">التاريخ</th>
              <th className="px-3 py-2 text-right font-medium text-gray-700">الوصف</th>
              <th className="px-3 py-2 text-right font-medium text-gray-700">البوابة</th>
              <th className="px-3 py-2 text-right font-medium text-gray-700">الإجمالي</th>
              <th className="px-3 py-2 text-right font-medium text-gray-700">الصافي</th>
              <th className="px-3 py-2 text-right font-medium text-gray-700">تاريخ الإيداع</th>
              <th className="px-3 py-2 text-right font-medium text-gray-700">الحالة</th>
              <th className="px-3 py-2 text-right font-medium text-gray-700">إجراءات</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {ordered.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center text-gray-500">
                  لا توجد عمليات حالياً.
                </td>
              </tr>
            ) : (
              ordered.map((t) => {
                const isPending =
                  !t.deposited && isAfter(startOfDay(new Date(t.deposit_date)), startOfDay(new Date()))
                const status = isPending ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-1 text-amber-800">
                    <CircleDot className="h-4 w-4" /> مؤجلة
                  </span>
                ) : t.deposited ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-1 text-emerald-800">
                    <CheckCircle2 className="h-4 w-4" /> مُودَع
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-1 text-emerald-800">
                    <CheckCircle2 className="h-4 w-4" /> مُودَع
                  </span>
                )

                const isRowEditing = editing?.id === t.id

                return (
                  <tr key={t.id}>
                    <td className="px-3 py-2 text-gray-700">{formatDate(t.created_at)}</td>

                    <td className="px-3 py-2">
                      {isRowEditing ? (
                        <input
                          value={editing.description}
                          onChange={(e) => setEditing((s) => (s ? { ...s, description: e.target.value } : s))}
                          className="w-full rounded border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
                          disabled={noEditMode}
                        />
                      ) : (
                        <span className="text-gray-800">{t.description || '-'}</span>
                      )}
                    </td>

                    <td className="px-3 py-2">
                      {isRowEditing ? (
                        <select
                          value={editing.gateway}
                          onChange={(e) => setEditing((s) => (s ? { ...s, gateway: e.target.value } : s))}
                          className="w-full rounded border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
                          disabled={noEditMode}
                        >
                          {gateways.map((g) => (
                            <option key={g.id} value={g.name}>
                              {g.name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className="text-gray-800">{t.gateway}</span>
                      )}
                    </td>

                    <td className="px-3 py-2">
                      {isRowEditing ? (
                        <input
                          type="number"
                          inputMode="decimal"
                          min="0"
                          step="0.01"
                          value={editing.amountGross}
                          onChange={(e) => setEditing((s) => (s ? { ...s, amountGross: e.target.value } : s))}
                          className="w-28 rounded border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
                          disabled={noEditMode}
                        />
                      ) : (
                        <span className="text-gray-800">{formatCurrency(t.amount_gross)}</span>
                      )}
                    </td>

                    <td className="px-3 py-2">
                      <span className="font-medium text-gray-900">{formatCurrency(t.net_amount)}</span>
                    </td>

                    <td className="px-3 py-2">
                      {isRowEditing ? (
                        <input
                          type="date"
                          value={editing.depositDate}
                          onChange={(e) => setEditing((s) => (s ? { ...s, depositDate: e.target.value } : s))}
                          className="w-40 rounded border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
                          disabled={noEditMode}
                        />
                      ) : (
                        <span className="text-gray-800">{formatDate(t.deposit_date)}</span>
                      )}
                    </td>

                    <td className="px-3 py-2">{status}</td>

                    <td className="px-3 py-2">
                      {isRowEditing ? (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={onSave}
                            disabled={noEditMode}
                            className="rounded bg-emerald-600 px-3 py-1 text-white hover:bg-emerald-700 disabled:opacity-50"
                          >
                            حفظ
                          </button>
                          <button
                            onClick={onCancel}
                            className="rounded bg-gray-200 px-3 py-1 text-gray-800 hover:bg-gray-300"
                          >
                            إلغاء
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => onEdit(t.id)}
                            disabled={noEditMode}
                            className="inline-flex items-center gap-1 rounded bg-indigo-600 px-3 py-1 text-white hover:bg-indigo-700 disabled:opacity-50"
                          >
                            <Edit3 className="h-4 w-4" /> تعديل
                          </button>
                          <button
                            onClick={() => onDelete(t.id)}
                            disabled={noEditMode}
                            className="inline-flex items-center gap-1 rounded bg-rose-600 px-3 py-1 text-white hover:bg-rose-700 disabled:opacity-50"
                          >
                            <Trash2 className="h-4 w-4" /> حذف
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
